import React from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSidebarStore } from '../stores/sidebarStore';
import { Home, MessageSquare, FileText, Database, Youtube, HelpCircle, Mail, MessageCircle, LogOut, Megaphone, ChevronLeft, ChevronRight, User, Users, Book, MailQuestion as QuestionMark } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { instanceId } = useParams();
  const { signOut } = useAuth();
  const { isCollapsed, toggleCollapsed } = useSidebarStore();

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (err) {
      console.error('Error al cerrar sesión:', err);
    }
  };

  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const menuItems = [
    { 
      type: 'section',
      label: 'PRINCIPAL',
      items: [
        { id: 'dashboard', label: 'Tablero Principal', icon: Home, path: '/' },
        { id: 'channels', label: 'Canales', icon: MessageSquare, path: '/channels' },
        { 
          id: 'contacts', 
          label: 'Contactos', 
          icon: Users, 
          path: instanceId ? `/contacts/${instanceId}` : '/contacts'
        },
        { 
          id: 'campaigns', 
          label: 'Campañas', 
          icon: Megaphone,
          path: instanceId ? `/campaigns/${instanceId}` : '/campaigns',
          disabled: !instanceId
        },
      ]
    },
    { 
      type: 'section',
      label: 'MI CUENTA',
      items: [
        { id: 'profile', label: 'Mi Perfil', icon: User, path: '/profile' },
        { id: 'api', label: 'Documentos de API', icon: FileText, path: '/api' },
      ]
    },
    { 
      type: 'section',
      label: 'AYUDA Y SOPORTE',
      items: [
        { id: 'help', label: 'Centro de Ayuda', icon: Book, path: '/help' },
        { id: 'faq', label: 'Preguntas Frecuentes', icon: QuestionMark, path: '/faq' },
        { id: 'contact', label: 'Contacto', icon: Mail, path: '/contact' },
      ]
    }
  ];

  return (
    <div className="min-h-screen flex bg-gray-100">
      {/* Sidebar */}
      <div 
        className={`${
          isCollapsed ? 'w-20' : 'w-64'
        } bg-[#2A3541] text-white transition-all duration-300 ease-in-out relative flex-shrink-0`}
      >
        {/* Toggle Button */}
        <button
          onClick={toggleCollapsed}
          className="absolute -right-3 top-20 bg-[#2A3541] text-white p-1 rounded-full shadow-lg hover:bg-gray-700 z-10"
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>

        <div className={`h-16 flex items-center ${isCollapsed ? 'justify-center' : 'px-6'} border-b border-gray-700`}>
          {isCollapsed ? (
            <img src="/logo-icon.png" alt="AgentIA" className="h-8 w-8" />
          ) : (
            <img src="/logo-full.png" alt="AgentIA" className="h-8" />
          )}
        </div>
        
        <nav className="py-4">
          {menuItems.map((section, index) => (
            <div key={index} className="mt-6 first:mt-0">
              {!isCollapsed && (
                <h3 className="px-6 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {section.label}
                </h3>
              )}
              <div className={`${isCollapsed ? '' : 'mt-2'}`}>
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.id}
                      onClick={() => navigate(item.path)}
                      disabled={item.disabled}
                      className={`w-full flex ${
                        isCollapsed ? 'justify-center' : 'items-center px-6'
                      } py-2 text-sm ${
                        active
                          ? 'bg-primary-blue text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                      } ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <Icon className={`h-5 w-5 ${!isCollapsed && 'mr-3'}`} />
                      {!isCollapsed && item.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Logout Button */}
        <div className={`absolute bottom-0 ${isCollapsed ? 'w-20' : 'w-64'} border-t border-gray-700`}>
          <button
            onClick={handleLogout}
            className={`w-full flex ${isCollapsed ? 'justify-center' : 'items-center px-6'} py-4 text-sm text-gray-300 hover:bg-gray-700`}
            title={isCollapsed ? 'Cerrar sesión' : undefined}
          >
            <LogOut className={`h-5 w-5 ${!isCollapsed && 'mr-3'}`} />
            {!isCollapsed && 'Cerrar sesión'}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-x-hidden">
        {children}
      </div>
    </div>
  );
}